import * as React from 'react';
import { AuthenticationContext } from './AuthenticationContext';
import { useEffect, useMemo, useState } from 'react';
import { User } from '@sentry/types';
import { UserManager, WebStorageStateStore } from 'oidc-client';

export interface OidcAuthProviderProps {
  clientId: string;
  authority: string;
  redirectUri: string;
  setUserInLogging?: (username: User) => void;
}

export const OidcAuthProvider: React.FC<OidcAuthProviderProps> = ({
  clientId,
  authority,
  redirectUri,
  children,
  setUserInLogging,
}) => {
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [initializing, setInitializing] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [email, setEmail] = useState('');
  const userManager = useMemo(
    () =>
      new UserManager({
        authority: authority,
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'id_token',
        scope: 'openid profile email',
        userStore: new WebStorageStateStore({ store: window.localStorage }),
      }),
    [authority, clientId, redirectUri]
  );

  useEffect(() => {
    userManager
      .getUser()
      .then((user) => {
        if (!user) {
          return;
        }
        setAuthenticated(true);
        setEmail(user?.profile?.email ?? '');
        setUserInLogging &&
          setUserInLogging({
            ...user.profile,
          });
      })
      .finally(() => setInitializing(false));
  }, []);

  const handleCallback = React.useCallback(
    () =>
      userManager.signinCallback().then((user) => {
        setAuthenticated(true);
        setEmail(user?.profile?.email ?? '');
        window.history.replaceState(
          {},
          window.document.title,
          window.location.origin + window.location.pathname
        );
        window.location = user.state || '/';
      }),
    [userManager]
  );

  const signIn = React.useCallback(() => {
    if (signingOut) {
      // Return non resolving promise in case APIs end up using this as we're signing out
      return new Promise<string>(() => { });
    }
    return userManager.signinRedirect({ state: window.location.href });
  }, [userManager, signingOut]);

  const getIdToken = React.useCallback(() => {
    if (signingOut) {
      // Return non resolving promise in case APIs end up using this as we're signing out
      return new Promise<string>(() => { });
    }
    return userManager
      .getUser()
      .then((user) => {
        return user.id_token;
      })
      .catch(() => {
        signIn();
        return new Promise<string>(() => { });
      });
  }, [userManager, signingOut]);

  const signOut = React.useCallback(() => {
    setSigningOut(true);
    localStorage.clear();
    window.location.href = window.location.origin;
  }, [userManager]);

  return (
    <AuthenticationContext.Provider
      value={{
        handleCallback,
        initializing,
        authenticated,
        getIdToken,
        signIn,
        signOut,
        email,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};
